import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { defaultInstance } from '../api/httpClient';
import { useBusinessContext } from '../context/BusinessContext';
import { useUserPermissions } from './useUserPermissions';

// Custom categories beyond adults/child (e.g., athlete, vip, spectator).
// Stored in the same JSONB as the existing fields. `key` is a stable slug used by
// consumers (public events page) to identify the category; titles are display labels.
//
// `is_main_guest`: when true, the first scanned guest of this category fills the
// access record's main_guest slot; everyone else (other categories, adults, children)
// becomes a companion. Only one category should be marked at a time — the admin UI
// enforces this. When no category is marked, the first adult is the main guest
// (legacy default).
export interface CustomCategory {
  key: string;
  min: number;
  max: number;
  title_en: string;
  title_es: string;
  subtitle_en?: string;
  subtitle_es?: string;
  is_main_guest?: boolean;
}

export interface CompanionSettings {
  short_name: string;
  title_en: string;
  title_es: string;
  adults: {
    min: number;
    max: number;
    title_en: string;
    title_es: string;
    subtitle_en: string;
    subtitle_es: string;
  };
  // `child` is optional — admins can delete the section entirely if children
  // shouldn't apply to this access setting (treat children as just another
  // custom category if they need fine-grained control).
  child?: {
    min_num: number;
    max_num: number;
    name_en: string;
    name_es: string;
    subtitle_en: string;
    subtitle_es: string;
  };
  categories?: CustomCategory[];
}

export interface AccessSetting {
  id: number;
  business_id: number;
  name: string;
  settings: {
    event: CompanionSettings[];
  };
}

// Prod contains legacy rows with neither a name nor any configured section;
// lists hide those instead of rendering blank entries (SAM-137).
export const hasEventConfig = (setting: AccessSetting): boolean => {
  const config = setting.settings?.event?.[0];
  return !!config && (!!config.adults || !!config.child || (config.categories?.length ?? 0) > 0);
};

export const isPopulatedAccessSetting = (setting: AccessSetting): boolean =>
  !!setting.name?.trim() || hasEventConfig(setting);

/**
 * The standalone list is only a fallback for the event dashboard, which reads access_settings inline from the event
 * (`expand=access_settings,access_settings_id`). The tenant Front Desk may not list the table (403, Logic: it only
 * gets it as an expand of event), so it never asks. The list is wanted when the event has loaded without it.
 */
export const needsStandaloneAccessSettings = (args: {
  eventLoaded: boolean;
  inlineSettings: unknown;
  isTenantFrontDesk: boolean;
}): boolean => args.eventLoaded && !args.inlineSettings && !args.isTenantFrontDesk;

export const useAccessSettings = (options: { enabled?: boolean } = {}) => {
  const { currentBusinessId } = useBusinessContext();
  const { isAdmin, isLoading: permissionsLoading } = useUserPermissions();

  return useQuery({
    queryKey: ['accessSettings', currentBusinessId, isAdmin],
    queryFn: async (): Promise<AccessSetting[]> => {
      let url = '/access_settings?limit=200';
      if (!isAdmin && currentBusinessId) {
        url += `&business_id=${currentBusinessId}`;
      }

      const response = await defaultInstance({ method: 'GET', url });
      return (response as any)?.data?.data ?? (response as any)?.data ?? [];
    },
    // Wait until permissions resolve so admins don't briefly fetch a
    // business-scoped list before isAdmin flips true.
    enabled: !permissionsLoading && (options.enabled ?? true),
    staleTime: 30000,
  });
};

export const useCreateAccessSetting = () => {
  const queryClient = useQueryClient();
  const { currentBusinessId } = useBusinessContext();

  return useMutation({
    mutationFn: async (newSetting: Omit<AccessSetting, 'id' | 'business_id'>) => {
      const response = await defaultInstance({
        method: 'POST',
        url: '/access_settings',
        data: { ...newSetting, business_id: currentBusinessId },
      });
      return (response as any)?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessSettings', currentBusinessId] });
    },
  });
};

export const useUpdateAccessSetting = () => {
  const queryClient = useQueryClient();
  const { currentBusinessId } = useBusinessContext();

  return useMutation({
    mutationFn: async (setting: AccessSetting) => {
      const { business_id, ...updatePayload } = setting;
      const response = await defaultInstance({
        method: 'PUT',
        url: `/access_settings/${setting.id}`,
        data: updatePayload,
      });
      return (response as any)?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessSettings', currentBusinessId] });
    },
  });
};

export const useDeleteAccessSetting = () => {
  const queryClient = useQueryClient();
  const { currentBusinessId } = useBusinessContext();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await defaultInstance({
        method: 'DELETE',
        url: `/access_settings/${id}`,
      });
      return (response as any)?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessSettings', currentBusinessId] });
    },
  });
};
